/**
 * CalendarPage - Top-level page for the calendar view of scheduled
 * Work Orders + Inspections.
 *
 * Visible to every authenticated user; the data feed is
 * server-side-scoped by role:
 *   - super_admin / developer        : every event platform-wide
 *   - admin / manager / company_admin: events for the user's companies
 *   - technician / reviewer          : only events the user is on
 *
 * Composition (atomic-design tiers in use):
 *   - organism EventFiltersBar    : top-of-page filters
 *   - organism CalendarView       : the calendar grid itself
 *   - organism EventDetailsPopover: rendered inside a Modal on click
 *   - organism Modal              : modal wrapper for the popover
 *
 * State is owned by the useScheduledEvents hook so this file stays
 * focused on layout. The popover lives inside a Modal because
 * react-big-calendar's built-in popup is too cramped for the action
 * row (Add to Calendar / Directions / Open record).
 */
import React, { useState } from 'react';
import {
    EventFiltersBar,
    CalendarView,
    EventDetailsPopover,
    Modal,
} from '../../components/organisms';
import { EventTypeBadge, Alert } from '../../components/atoms';
import { useScheduledEvents } from '../../hooks/useScheduledEvents';
import {
    getEventCategory,
    type ScheduledEvent,
} from '../../services/scheduledEventsService';
import { PageContainer } from '../../components/templates/PageContainer';

const CalendarPage: React.FC = () => {
    const {
        filters,
        setFilters,
        events,
        isLoading,
        error,
        customerOptions,
        assigneeOptions,
        cityOptions,
        stateOptions,
    } = useScheduledEvents();

    /* Selected event(s) drive the popover modal. Null = closed. A
     * single click can open either one event (most cases) or a
     * grouped project bucket; we model both as the same nullable
     * array — `null` is closed, `[e]` is a single, `[a, b, ...]` is a
     * grouped chip showing a list. */
    const [selectedEvents, setSelectedEvents] = useState<ScheduledEvent[] | null>(null);
    /* When the user picks one entry inside a grouped list, drill into
     * its detail popover; back-arrow returns to the list. */
    const [drilldownEvent, setDrilldownEvent] = useState<ScheduledEvent | null>(null);

    return (
        <PageContainer block="calendar-page" dataRef="page-calendar-page" width="wide">
            <div className="mb-16px">
                <h1 className="text-24px tablet:text-28px font-semibold text-brand-text">Calendar</h1>
                <p className="text-14px text-brand-text-muted mt-4px">
                    Scheduled work orders and inspections you can see.
                </p>
            </div>

            {/* Filter bar — shared with the Map page */}
            <EventFiltersBar
                value={filters}
                onChange={setFilters}
                customerOptions={customerOptions}
                assigneeOptions={assigneeOptions}
                cityOptions={cityOptions}
                stateOptions={stateOptions}
                className="mb-16px"
            />

            {/* Loading / error banners */}
            {isLoading && (
                <div className="text-13px text-brand-text-muted py-8px">Loading events…</div>
            )}
            {error && (
                <Alert variant="error" className="mb-12px">{error}</Alert>
            )}

            {/* Calendar grid */}
            <CalendarView
                events={events}
                onSelectEvent={(selection) => {
                    setDrilldownEvent(null);
                    if (selection.kind === 'single') {
                        setSelectedEvents([selection.event]);
                    } else {
                        setSelectedEvents(selection.events);
                    }
                }}
            />

            {/* Detail modal — single event renders detail directly;
             * grouped chips render a list with click-to-drill behavior. */}
            <Modal
                isOpen={selectedEvents !== null}
                onClose={() => {
                    setSelectedEvents(null);
                    setDrilldownEvent(null);
                }}
                title={
                    drilldownEvent
                        ? 'Event details'
                        : selectedEvents && selectedEvents.length > 1
                        ? `Project schedule (${selectedEvents.length})`
                        : 'Event details'
                }
                size="md"
            >
                {selectedEvents && (
                    selectedEvents.length === 1 || drilldownEvent ? (
                        <>
                            {drilldownEvent && selectedEvents.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setDrilldownEvent(null)}
                                    className="mb-8px inline-flex items-center gap-4px text-13px text-brand-primary hover:underline"
                                >
                                    <span aria-hidden="true">←</span> Back to list
                                </button>
                            )}
                            <EventDetailsPopover
                                event={drilldownEvent ?? selectedEvents[0]}
                            />
                        </>
                    ) : (
                        <GroupedEventList
                            events={selectedEvents}
                            onSelect={(event) => setDrilldownEvent(event)}
                        />
                    )
                )}
            </Modal>
        </PageContainer>
    );
};

/**
 * Compact list of events shown inside the modal when the user clicks
 * a grouped project chip. Selecting a row drills into the standard
 * EventDetailsPopover for that one event.
 */
interface GroupedEventListProps {
    events: ScheduledEvent[];
    onSelect: (event: ScheduledEvent) => void;
}

const GroupedEventList: React.FC<GroupedEventListProps> = ({ events, onSelect }) => {
    const formatTime = (iso: string, allDay: boolean) => {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        if (allDay) return 'All day';
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };

    return (
        <ul className="divide-y divide-brand-border">
            {events.map((event) => (
                <li key={event.id}>
                    <button
                        type="button"
                        onClick={() => onSelect(event)}
                        className="w-full text-left py-10px px-4px flex items-start gap-12px hover:bg-brand-surface rounded-md"
                    >
                        <EventTypeBadge type={getEventCategory(event)} size="xs" />
                        <div className="flex-1 min-w-0">
                            <div className="text-14px font-sans font-semibold text-brand-text truncate">
                                {event.title}
                            </div>
                            <div className="text-12px text-brand-text-muted mt-2px">
                                {formatTime(event.start, event.allDay)}
                                {event.locationName ? ` • ${event.locationName}` : ''}
                                {event.assignees.length > 0
                                    ? ` • ${event.assignees.map((a) => a.name).join(', ')}`
                                    : ''}
                            </div>
                        </div>
                        <span aria-hidden="true" className="text-brand-text-muted">›</span>
                    </button>
                </li>
            ))}
        </ul>
    );
};

export default CalendarPage;
